import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { Icon, habitGlyph } from './icons/Icon';
import type { Habit } from '../types';
interface HabitItemProps {
  habit: Habit;
  completed: boolean;
  /** Omit to render a status-only row (completion driven elsewhere). */
  onToggle?: () => void;
  /** Overrides the static goal line (e.g. the personalized hydration target). */
  goal?: string;
}
export function HabitItem({
  habit,
  completed,
  onToggle,
  goal
}: HabitItemProps) {
  const {
    t
  } = useLocale();
  const interactive = Boolean(onToggle);
  return <motion.div role={interactive ? 'button' : undefined} tabIndex={interactive ? 0 : undefined} onClick={onToggle} onKeyDown={interactive ? event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle?.();
    }
  } : undefined} aria-pressed={interactive ? completed : undefined} whileTap={interactive ? {
    scale: 0.985
  } : undefined} transition={{
    duration: 0.14,
    ease: [0.23, 1, 0.32, 1]
  }} className={`flex w-full items-center gap-2 rounded-card border px-2 py-2 text-left transition-[background-color,border-color] duration-150 ease-brand ${interactive ? 'cursor-pointer' : ''} ${completed ? 'border-gold/45 bg-sand-deep/70' : `border-sand-line bg-white/70 ${interactive ? 'hover:border-deep/25' : ''}`}`} data-id="element-647" data-mp="kvcjv5hx8un8">
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-[13px] transition-colors duration-150 ease-brand ${completed ? 'bg-deep text-gold' : 'bg-sand-deep text-soft'}`} data-id="element-648" data-mp="ckwobprw8faj">
        <Icon name={habitGlyph[habit.icon]} size="md" data-id="element-649" data-mp="2kw2nx6jxn36" />
      </span>

      <span className="min-w-0 flex-1" data-id="element-650" data-mp="3uqj5iaktwlh2">
        <span className="block truncate text-[14px] font-medium text-deep" data-id="element-651" data-mp="3urkt1ej170fo">
          {t(`habit.${habit.id}.title`)}
        </span>
        <span className="block truncate text-[11px] text-muted" data-id="element-652" data-mp="3p37lbi1dtr3b">
          {goal ?? t(`habit.${habit.id}.goal`)}
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-2" data-id="element-653" data-mp="3b4zorb89crso">
        <span className={`bee-num text-[11px] font-bold ${completed ? 'text-gold-deep' : 'text-muted'}`} data-id="element-654" data-mp="3g2hoqc4arwoj">
          {habit.xp} {t('common.xp')}
        </span>
        <span className={`grid h-6 w-6 place-items-center rounded-pill border transition-colors duration-150 ease-brand ${completed ? 'border-gold bg-gold text-deep' : 'border-deep/25'}`} data-id="element-655" data-mp="bp2smlta9t0v">
          <AnimatePresence initial={false} data-id="element-656" data-mp="2uy5odh9t1qm">
            {completed && <motion.span initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.14,
            ease: [0.23, 1, 0.32, 1]
          }} className="grid place-items-center" data-id="element-657" data-mp="qlk37atnf3md">
                <CheckIcon size={14} strokeWidth={3} data-id="element-658" data-mp="10oz6fvagrq6c" />
              </motion.span>}
          </AnimatePresence>
        </span>
      </span>
    </motion.div>;
}